import React, { useEffect, useState } from "react";
import { collection, limit, onSnapshot, orderBy, query } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { db } from "../../firebase";
import { CloseIcon } from "../util/Icons";
import { shouldShowWeeklyRecapBanner } from "./weeklyRecapBannerUtils";

function getRecapKey(recap) {
  return recap?.week?.weekKey || recap?.id || "";
}

function getFirstName(userDetails) {
  return String(userDetails?.firstName || "").trim().split(/\s+/)[0] || "";
}

function getBannerHeadline(recap, userDetails) {
  const headline = recap?.display?.headline || "Your Week in Motion";
  const firstName = getFirstName(userDetails);
  if (headline === "Your Week in Motion" && firstName) {
    return `${headline} ${firstName}`;
  }
  return headline;
}

function getHighlightDateLabel(dayKey) {
  if (!dayKey) return "";
  const date = new Date(`${dayKey}T12:00:00Z`);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  });
}

function getHighlightTitle(highlight) {
  const title = highlight?.title || "";
  const dateLabel = getHighlightDateLabel(highlight?.dayKey);
  if (!title || !dateLabel || title.includes(dateLabel)) {
    return title;
  }
  return `${title}, ${dateLabel}`;
}

function getVisibleStats(stats = []) {
  return stats.filter((stat) => String(stat?.label || "").trim().toLowerCase() !== "race progress");
}

function formatDistance(value) {
  const distance = Math.round((Number(value) || 0) * 10) / 10;
  return `${distance.toFixed(distance >= 10 ? 0 : 1)} km`;
}

function formatInteger(value) {
  return String(Math.round(Number(value) || 0));
}

function formatPercent(value) {
  return `${Math.round(Number(value) || 0)}%`;
}

function getPersonalTeamStats(recap) {
  const featuredRace = recap?.featuredRace;
  if (recap?.recapType !== "personal_team" || featuredRace?.mode !== "team") {
    return [];
  }

  return [
    { label: "Team distance", value: formatDistance(featuredRace.teamWeeklyDistanceKm) },
    { label: "Active teammates", value: formatInteger(featuredRace.teamActiveMembersCount) },
    { label: "Race progress", value: formatPercent(featuredRace.percentComplete) },
  ];
}

function getStatClassName(label = "") {
  const normalized = String(label).trim().toLowerCase();
  if (normalized === "active days") {
    return "border-green-200 bg-gradient-to-r from-[#DDFCE6] to-[#86EFAC] text-green-950";
  }
  if (normalized === "distance") {
    return "border-blue-200 bg-gradient-to-r from-[#DBEAFE] to-[#7DD3FC] text-blue-950";
  }
  if (normalized === "time") {
    return "border-orange-200 bg-gradient-to-r from-[#FFEDD5] to-[#FDBA74] text-orange-950";
  }
  if (normalized === "team distance") {
    return "border-teal-200 bg-gradient-to-r from-[#CCFBF1] to-[#5EEAD4] text-teal-950";
  }
  if (normalized === "active teammates") {
    return "border-violet-200 bg-gradient-to-r from-[#EDE9FE] to-[#C4B5FD] text-violet-950";
  }
  if (normalized === "race progress") {
    return "border-sky-200 bg-gradient-to-r from-[#E0F2FE] to-[#93C5FD] text-sky-950";
  }
  return "border-slate-100 bg-slate-50 text-slate-900";
}

function getRaceFromMap(userRaces, legId) {
  if (!userRaces || !legId) return null;
  if (typeof userRaces.get === "function") return userRaces.get(legId) || null;
  return userRaces[legId] || null;
}

function getRaceLogoUrl(featuredRace, userRaces) {
  const race = getRaceFromMap(userRaces, featuredRace?.legId);
  return featuredRace?.imageUrl || race?.raceInfo?.imageUrl || race?.imageUrl || "";
}

function getRaceInitials(name = "") {
  return String(name)
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0])
    .join("")
    .toUpperCase();
}

function ProgressCircle({ percent }) {
  const value = Math.max(0, Math.min(100, Math.round(Number(percent) || 0)));
  return (
    <div
      className="grid h-12 w-12 shrink-0 place-items-center rounded-full sm:h-14 sm:w-14"
      style={{ background: `conic-gradient(#006BC1 0 ${value}%, #E5E7EB ${value}% 100%)` }}
      aria-label={`${value}% complete`}
      title={`${value}% complete`}
    >
      <div className="grid h-9 w-9 place-items-center rounded-full bg-white sm:h-10 sm:w-10">
        <span className="text-[11px] font-bold text-slate-900 sm:text-xs">{value}%</span>
      </div>
    </div>
  );
}

export default function WeeklyRecapBanner({ user, userDetails, userRaces }) {
  const [recap, setRecap] = useState(null);
  const [optimisticallyDismissedKey, setOptimisticallyDismissedKey] = useState("");
  const [dismissLoading, setDismissLoading] = useState(false);
  const [dismissError, setDismissError] = useState("");

  useEffect(() => {
    if (!user?.uid) {
      setRecap(null);
      setOptimisticallyDismissedKey("");
      return undefined;
    }

    const recapsQuery = query(
      collection(db, "user_activities", user.uid, "weekly_recaps"),
      orderBy("generatedAt", "desc"),
      limit(1)
    );

    return onSnapshot(
      recapsQuery,
      (snapshot) => {
        const latestDoc = snapshot.docs[0];
        const latestRecap = latestDoc ? { ...latestDoc.data(), id: latestDoc.id } : null;
        setRecap(latestRecap);
        setOptimisticallyDismissedKey((currentKey) => {
          const dismissalWasConfirmed = latestRecap?.banner?.dismissedAt && getRecapKey(latestRecap) === currentKey;
          const latestRecapChanged = getRecapKey(latestRecap) !== currentKey;
          return dismissalWasConfirmed || latestRecapChanged ? "" : currentKey;
        });
        setDismissError("");
      },
      (error) => {
        console.error("Unable to load weekly recap", error);
        setRecap(null);
      }
    );
  }, [user?.uid]);

  const recapKey = getRecapKey(recap);
  if (!shouldShowWeeklyRecapBanner(recap) || recapKey === optimisticallyDismissedKey) {
    return null;
  }

  const stats = getVisibleStats(Array.isArray(recap?.display?.stats) ? recap.display.stats : []);
  const personalTeamStats = getPersonalTeamStats(recap);
  const highlight = recap?.personal?.highlight;
  const featuredRace = recap?.featuredRace;
  const isTeamOnly = recap?.recapType === "team";
  const raceLogoUrl = getRaceLogoUrl(featuredRace, userRaces);

  async function dismissRecap() {
    const weekKey = recapKey;
    if (!weekKey || dismissLoading) return;

    // Hide the recap immediately; the callable persists that choice in the background.
    setOptimisticallyDismissedKey(weekKey);
    setDismissLoading(true);
    setDismissError("");

    try {
      const dismissWeeklyRecap = httpsCallable(getFunctions(), "dismissWeeklyRecap");
      await dismissWeeklyRecap({ weekKey });
    } catch (error) {
      console.error("Unable to dismiss weekly recap", error);
      setOptimisticallyDismissedKey("");
      setDismissError("Unable to dismiss this right now.");
    } finally {
      setDismissLoading(false);
    }
  }

  return (
    <section className="w-full max-w-3xl mx-auto my-3 rounded-xl border border-slate-200 bg-white shadow-md overflow-hidden">
      <div className="flex items-start justify-between gap-3 px-4 py-3 bg-[#F8FBFD] border-b border-slate-100">
        <div>
          <p className="m-0 text-xs font-semibold uppercase tracking-wide text-[#006BC1]">Weekly Recap</p>
          <h2 className="m-0 mt-1 text-xl font-bold leading-tight text-slate-900">
            {getBannerHeadline(recap, userDetails)}
          </h2>
          {recap?.week?.label && <p className="m-0 mt-1 text-xs font-medium text-slate-500">{recap.week.label}</p>}
        </div>
        <button
          type="button"
          onClick={dismissRecap}
          disabled={dismissLoading}
          className="shrink-0 rounded-full p-1.5 text-slate-500 hover:bg-white hover:text-slate-800 disabled:opacity-60"
          aria-label="Dismiss weekly recap"
          title="Dismiss"
        >
          <CloseIcon size={18} />
        </button>
      </div>

      <div className="px-4 py-3">
        {stats.length > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3">
            {stats.map((stat) => (
              <div key={`${stat.label}-${stat.value}`} className={`rounded-lg border px-3 py-2 ${getStatClassName(stat.label)}`}>
                <p className="m-0 text-[11px] font-semibold uppercase tracking-wide opacity-75">{stat.label}</p>
                <p className="m-0 mt-1 text-base font-bold">{stat.value}</p>
              </div>
            ))}
          </div>
        )}

        {personalTeamStats.length > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3">
            {personalTeamStats.map((stat) => (
              <div key={`${stat.label}-${stat.value}`} className={`rounded-lg border px-3 py-2 ${getStatClassName(stat.label)}`}>
                <p className="m-0 text-[11px] font-semibold uppercase tracking-wide opacity-75">{stat.label}</p>
                <p className="m-0 mt-1 text-base font-bold">{stat.value}</p>
              </div>
            ))}
          </div>
        )}

        {!isTeamOnly && highlight && (
          <div className="mb-3 rounded-lg bg-gradient-to-r from-[#6D5DF6] to-[#A855F7] px-3 py-2 text-sm text-white">
            <p className="m-0 font-semibold">{getHighlightTitle(highlight)}</p>
            {highlight.body && <p className="m-0 mt-1">{highlight.body}</p>}
          </div>
        )}

        {featuredRace && (
          <div className="mb-3 flex items-center justify-between gap-2 rounded-lg border border-slate-100 px-3 py-2">
            <div className="flex min-w-0 flex-1 items-center gap-2 sm:gap-3">
              <div className="grid h-10 w-10 shrink-0 place-items-center overflow-hidden rounded-full bg-white sm:h-12 sm:w-12">
                {raceLogoUrl ? (
                  <img src={raceLogoUrl} alt={`${featuredRace.raceName} logo`} className="h-full w-full object-contain" />
                ) : (
                  <span className="text-xs font-semibold text-slate-500">{getRaceInitials(featuredRace.raceName)}</span>
                )}
              </div>
              <div className="min-w-0">
                <p className="m-0 truncate text-sm font-semibold leading-tight text-slate-900">{featuredRace.raceName}</p>
                <p className="m-0 text-xs text-slate-500">
                  {featuredRace.mode === "team" ? `${featuredRace.teamName} team progress` : "Race progress"}
                </p>
              </div>
            </div>
            <ProgressCircle percent={featuredRace.percentComplete} />
          </div>
        )}

        {dismissError && <p className="mb-2 text-sm font-medium text-red-600">{dismissError}</p>}

        {/* Share button paused until image sharing is ready. */}
      </div>
    </section>
  );
}
